import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useAuthorProfile } from "../../../hooks/use-author-profile";
import { useBroadcastScore } from "../../../hooks/use-broadcast-score";
import { useCurrentMinute } from "../../../hooks/use-current-minute";
import { useCurrentUser } from "../../../hooks/use-current-user";
import { useElapsedDuration } from "../../../hooks/use-elapsed-duration";
import { useViewerCount } from "../../../hooks/use-viewer-count";
import {
	correctTimeRequest,
	endBroadcastRequest,
	fullTimeRequest,
	halfTimeRequest,
	kickoffRequest,
	secondHalfRequest,
	varReviewRequest,
} from "../../../redux/broadcast/broadcast.actions";
import {
	selectBroadcastId,
	selectFullTimeAt,
	selectHalfTimeAt,
	selectSecondHalfStartTime,
	selectStartTime,
	selectTimeCorrection,
} from "../../../redux/broadcast/broadcast.selectors";
import { showModal } from "../../../redux/modals/actions";
import type { ToolbarOptionConfig } from "../../toolbar-dropdown/toolbar-dropdown.types";
import CorrectTime from "./correct-time/correct-time";
import LiveStatus from "./live-status/live-status";
import * as CSS from "./toolbar-controls.styles";
import { getBroadcastOptions, getMatchOptions } from "./toolbar-controls.utils";
import ToolbarStat from "./toolbar-stat/toolbar-stat";

const ToolbarControls = () => {
	const [activeMenu, setActiveMenu] = useState<string | null>(null);
	const [newMatchTime, setNewMatchTime] = useState<number | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	// onConfirm below is a closure snapshotted into redux state at dispatch
	// time (same reason CorrectTime can't stay controlled by a prop) — it
	// can't see later setNewMatchTime updates, so the latest typed value is
	// mirrored here and read at confirm time instead of closed over.
	const newMatchTimeRef = useRef<number | null>(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const broadcastId = useSelector(selectBroadcastId);
	const startTime = useSelector(selectStartTime);
	const halfTimeAt = useSelector(selectHalfTimeAt);
	const secondHalfStartTime = useSelector(selectSecondHalfStartTime);
	const fullTimeAt = useSelector(selectFullTimeAt);
	const timeCorrection = useSelector(selectTimeCorrection);
	const { varReview, createdAt, endedAt } = useBroadcastScore(broadcastId);
	const viewerCount = useViewerCount(broadcastId);
	const currentUser = useCurrentUser();
	const { followerCount } = useAuthorProfile(currentUser?.uid ?? null);
	const currentMinute = useCurrentMinute(
		startTime,
		halfTimeAt,
		secondHalfStartTime,
		timeCorrection,
	);
	const broadcastDuration = useElapsedDuration(createdAt?.toMillis() ?? null);
	const isEnded = !!endedAt;

	// Once the broadcast we're tracking ends (manually or via the auto-end
	// scheduler), send the author to the read-only stats page — it's driven
	// purely by the :id route param, not this redux slice. The slice itself
	// (and localStorage's broadcastState) gets cleared by BroadcastStats
	// once it mounts, not here — BroadcastPortal has its own
	// `if (!broadcastId) return <Navigate to="/new" />` guard, and clearing
	// broadcastId from this effect raced that guard and lost every time.
	useEffect(() => {
		if (!broadcastId || !isEnded) return;
		navigate(`/broadcast/${broadcastId}/stats`);
	}, [broadcastId, isEnded, navigate]);

	const matchTimeValue = !startTime
		? "—"
		: fullTimeAt
			? "Full time"
			: halfTimeAt && !secondHalfStartTime
				? "Half time"
				: currentMinute !== null
					? `${currentMinute}m`
					: "—";

	const options: Record<string, ToolbarOptionConfig[]> = {
		broadcast: getBroadcastOptions(
			!broadcastId ? "upcoming" : isEnded ? "ended" : "live",
		),
		match: getMatchOptions(
			startTime,
			halfTimeAt,
			secondHalfStartTime,
			fullTimeAt,
			varReview,
		),
	};

	const handleMenuOpen = useCallback((id: string) => {
		setActiveMenu((prev) => (prev === id ? null : id));
	}, []);

	useEffect(() => {
		if (activeMenu === null) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setActiveMenu(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [activeMenu]);

	const handleMenuItemSelected = useCallback(
		(id: string) => {
			setActiveMenu(null);

			if (id === "kick-off") {
				if (!broadcastId) return;
				dispatch(kickoffRequest({ broadcastId }));
			}

			if (id === "half-time") {
				if (!broadcastId) return;
				dispatch(halfTimeRequest({ broadcastId }));
			}

			if (id === "second-half") {
				if (!broadcastId) return;
				dispatch(secondHalfRequest({ broadcastId }));
			}

			if (id === "full-time") {
				if (!broadcastId) return;
				dispatch(fullTimeRequest({ broadcastId }));
			}

			if (id === "var-review") {
				if (!broadcastId) return;
				dispatch(varReviewRequest({ broadcastId, active: !varReview }));
			}

			if (id === "end-broadcast") {
				if (!broadcastId) return;
				dispatch(
					showModal({
						id: "end-broadcast",
						onConfirm: () => dispatch(endBroadcastRequest({ broadcastId })),
						title: "End broadcast",
						description: "Are you sure you want to end this broadcast?",
					}),
				);
			}

			if (id === "time-correction") {
				if (!broadcastId) return;
				const initialMatchTime = newMatchTime || currentMinute || 1;
				newMatchTimeRef.current = initialMatchTime;
				dispatch(
					showModal({
						id: "time-correction",
						onConfirm: () => {
							dispatch(
								correctTimeRequest({
									broadcastId,
									minute: newMatchTimeRef.current
										? newMatchTimeRef.current === 0
											? 1
											: newMatchTimeRef.current - 1
										: initialMatchTime,
								}),
							);
							// Correction lands in redux via the saga's setTimeCorrection,
							// so currentMinute already reflects it next render — clearing
							// this lets that flow back in as the seed next time the modal opens.
							setNewMatchTime(null);
						},
						title: "Match time correction",
						description:
							"Sometimes the match your watching falls behind, or you're simply out of sync with the real world. Use this to correct the match time to your broadcast.",
						disabled: initialMatchTime <= 0,
						children: (
							<CorrectTime
								initialMatchTime={initialMatchTime}
								onChange={(value) => {
									newMatchTimeRef.current = value;
									setNewMatchTime(value);
								}}
							/>
						),
					}),
				);
			}

			if (id === "copy-link") {
				if (!broadcastId) return;
				navigator.clipboard.writeText(
					`${window.location.origin}/broadcast/${broadcastId}`,
				);
			}
		},
		[dispatch, broadcastId, varReview, newMatchTime, currentMinute],
	);

	if (isEnded) return null;

	return (
		<CSS.ToolbarControls ref={containerRef}>
			<LiveStatus />
			<ToolbarStat
				id="broadcast"
				label="Broadcast"
				value={broadcastDuration ?? "—"}
				options={options.broadcast}
				isMenuOpen={activeMenu === "broadcast"}
				onMenuOpen={handleMenuOpen}
				onMenuItemSelected={handleMenuItemSelected}
			/>
			<ToolbarStat
				id="match"
				label="Match time"
				value={matchTimeValue}
				options={options.match}
				isMenuOpen={activeMenu === "match"}
				onMenuOpen={handleMenuOpen}
				onMenuItemSelected={handleMenuItemSelected}
			/>
			<ToolbarStat id="viewers" label="Viewers" value={String(viewerCount)} />
			<ToolbarStat
				id="followers"
				label="Followers"
				value={String(followerCount)}
			/>
		</CSS.ToolbarControls>
	);
};

export default ToolbarControls;
